import { useCallback, useEffect } from 'react';

import { ApolloError, useQuery } from '@apollo/client';

import { TapestryJobStatus, TapestryJobStatusVariables } from './__generated__/operations';
import { TAPESTRY_JOB_STATUS_QUERY } from './operations';
import { TapestryCompletionOutcome, TapestryStage, isInProgressOutcome } from './stages';

type TapestryJobStatus_tapestryJobStatus = NonNullable<TapestryJobStatus>['tapestryJobStatus'];
// Within the design's "every 2–5s while the pipeline is active" window.
export const TAPESTRY_POLL_INTERVAL_MS = 3000;

export interface UseTapestryJobResult {
  job: TapestryJobStatus_tapestryJobStatus | undefined;
  stage: TapestryStage | undefined;
  completionOutcome: TapestryCompletionOutcome | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * Force a one-off refresh — e.g. immediately after a gate mutation resolves.
   * Returns the in-flight promise so callers can await it and recover (e.g.
   * re-enable a gate button) if the refresh itself fails.
   */
  refetch: () => Promise<unknown>;
}

/**
 * Polls `tapestryJobStatus` for a file while the pipeline is actively
 * processing, and stops once it pauses at a human gate or reaches a terminal
 * outcome. Pass `skip` (e.g. for legacy, non-Tapestry uploads) to keep the
 * query dormant so flag-off behavior is unchanged.
 *
 * Polling is driven by `completion_outcome === 'in_progress'` (not the
 * `current_stage` allow-list) so transient stages like VALIDATED / PERSISTED —
 * which sit between the named pipeline stages — don't prematurely stop polling.
 * It still pauses at the two human gates (WAITING_FOR_MAPPING /
 * WAITING_FOR_REVIEW), where the server state only changes on a user mutation;
 * callers `refetch()` after submitting a gate.
 */
export function useTapestryJob(
  fileId: string | undefined,
  options?: { skip?: boolean },
): UseTapestryJobResult {
  const effectiveSkip = (options?.skip ?? false) || !fileId;

  const { data, loading, error, startPolling, stopPolling, refetch } = useQuery<
    TapestryJobStatus,
    TapestryJobStatusVariables
  >(TAPESTRY_JOB_STATUS_QUERY, {
    variables: { fileId: fileId ?? '' },
    skip: effectiveSkip,
    // Every poll must hit the network so the stage stays live. Do NOT add
    // nextFetchPolicy: 'cache-first' — on Apollo 3.7.x it makes startPolling
    // serve stale cache after the first fetch (apollographql/apollo-client#10222),
    // so the stage would never update.
    fetchPolicy: 'network-only',
  });

  // Apollo can briefly surface the previous file's job during a `fileId` change:
  // with fetchPolicy 'network-only' the hook keeps the prior `data` while the
  // new query is in flight. Discard the job unless it's for the requested file,
  // so consumers (the gates, the live stepper, the terminal outcome panel and
  // its Pool CTA) never act on another tape's stage or terminal outcome during
  // that window — they render nothing until the routed tape's job resolves.
  const matchedData = data?.tapestryJobStatus?.file_id === fileId ? data : undefined;
  const job = matchedData?.tapestryJobStatus;
  const stage = job?.current_stage as TapestryStage | undefined;
  const completionOutcome = job?.completion_outcome as TapestryCompletionOutcome | undefined;
  // A human gate truly pauses the run only when the server is actually waiting on
  // the user. WAITING_FOR_MAPPING is actionable as soon as it's reached, but
  // WAITING_FOR_REVIEW can be set a beat before `review.ready_for_review` flips —
  // so keep polling through that window. Stopping at WAITING_FOR_REVIEW while
  // ready_for_review is still false would strand the run: the review gate (which
  // mounts on ready_for_review) would never appear and polling would never resume.
  const pausedAtGate =
    stage === 'WAITING_FOR_MAPPING' ||
    (stage === 'WAITING_FOR_REVIEW' && !!job?.review?.ready_for_review);
  // Keep polling while the run is in progress, except when paused at a gate where
  // the server won't advance until the user acts (we refetch on the gate mutation).
  const active = !effectiveSkip && isInProgressOutcome(completionOutcome) && !pausedAtGate;

  useEffect(() => {
    if (active) {
      startPolling(TAPESTRY_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
    // `fileId` is in the deps so switching between two tapes that are both in
    // an active stage tears down and restarts polling for the new file rather
    // than continuing to poll the previous one.
  }, [active, fileId, startPolling, stopPolling]);

  // Stable identity so consumers can list it in effect deps without the effect
  // re-running every render. Apollo's `refetch` is itself stable; the wrapper
  // only normalizes the return to `Promise<unknown>` and drops the variables
  // arg. Without useCallback this was a fresh closure each render, which made
  // TapeUploadFlow's resume-after-gate effect re-fire (overlapping refetches)
  // on every render instead of once per poll / stage change.
  const stableRefetch = useCallback(() => refetch(), [refetch]);

  return {
    job,
    stage,
    completionOutcome,
    loading,
    error,
    refetch: stableRefetch,
  };
}
